import { useNavigate } from "react-router-dom";
import { type Language, getPageTranslations, getTranslation } from "../lib/translations";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Briefcase, GraduationCap, Heart, Users2, Mail, ArrowUpRight } from "lucide-react";
import { professionalTimeline, academicTimeline, communityTimeline } from "../lib/config";
import { useDynamicContent, getTimelineEvents } from "../lib/dynamicContent";
import { HomeSkeleton } from "./ui/Skeletons";
import { TestimonialsSection } from "./TestimonialsSection";
import { SimulatedLoading } from "./ui/SimulatedLoading";
import { LazyRevealSection } from "./ui/LazyRevealSection";
import { AutoFitPunchline } from "./AutoFitPunchline";

interface HomeTabProps {
  lang: Language;
}

interface HighlightItem {
  tab: string;
  title: string;
  content: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

function HighlightCard({ item, onClick, className = "" }: { item: HighlightItem; onClick: () => void; className?: string }) {
  const Icon = item.icon;
  return (
    <Card
      onClick={onClick}
      glowing
      className={`group cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-brand-primary/10 flex flex-col justify-between p-6 rounded-2xl border border-border/60 ${className}`}
    >
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 w-full">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-primary/5 dark:bg-brand-primary/10 border border-brand-primary/15 text-brand-primary group-hover:bg-brand-primary group-hover:text-white transition-all duration-300">
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest block">
              {item.title}
            </span>
            <h4 className="text-base font-extrabold text-foreground mt-1 leading-snug group-hover:text-brand-primary transition-colors">
              {item.content}
            </h4>
          </div>
        </div>
        <ArrowUpRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-all text-brand-primary md:self-center" />
      </div>
      <p className="text-xs text-muted-foreground mt-3 leading-relaxed font-sans max-w-xl">
        {item.description}
      </p>
    </Card>
  );
}

function getHighlightOverride(settings: any[], key: string, lang: string): string | null {
  const configSetting = settings.find((s) => s.key === "home_highlights_config")?.value;
  if (!configSetting) return null;
  try {
    const parsed = JSON.parse(configSetting);
    const mapKey = key
      .replace("home_highlight_job_", "job_")
      .replace("home_highlight_acad_", "acad_")
      .replace("home_highlight_hobby_", "hobby_")
      .replace("home_highlight_comm_", "comm_");
    return parsed[mapKey];
  } catch {
    return null;
  }
}

interface HighlightsGridProps {
  highlights: HighlightItem[];
  t: any;
  lang: string;
  navigate: (path: string) => void;
}

function HighlightsGrid({ highlights, t, lang, navigate }: HighlightsGridProps) {
  return (
    <section className="space-y-6">
      <div className="border-b border-border/40 pb-4">
        <h3 className="text-2xl font-extrabold tracking-tight text-foreground">{t.highlightsTitle}</h3>
        <p className="text-sm text-muted-foreground mt-1">{t.highlightsSubtitle}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {highlights[0] && (
          <HighlightCard
            item={highlights[0]}
            className="md:col-span-2"
            onClick={() => navigate(`/${lang}/${highlights[0]?.tab || ""}`)}
          />
        )}
        {highlights[1] && (
          <HighlightCard
            item={highlights[1]}
            className="md:col-span-1"
            onClick={() => navigate(`/${lang}/${highlights[1]?.tab || ""}`)}
          />
        )}
        {highlights[2] && (
          <HighlightCard
            item={highlights[2]}
            className="md:col-span-1"
            onClick={() => navigate(`/${lang}/${highlights[2]?.tab || ""}`)}
          />
        )}
        {highlights[3] && (
          <HighlightCard
            item={highlights[3]}
            className="md:col-span-2"
            onClick={() => navigate(`/${lang}/${highlights[3]?.tab || ""}`)}
          />
        )}
      </div>
    </section>
  );
}

function buildHighlights(
  t: any,
  settings: any[],
  lang: string,
  latestJob: any,
  latestEducation: any,
  latestVolunteering: any
): HighlightItem[] {
  return [
    {
      tab: "professional",
      title: t.latestJobTitle,
      content:
        getHighlightOverride(settings, `home_highlight_job_content_${lang}`, lang) ||
        (latestJob
          ? `${getTranslation(latestJob.role, lang)} @ ${getTranslation(latestJob.company, lang)}`
          : ""),
      description:
        getHighlightOverride(settings, `home_highlight_job_desc_${lang}`, lang) ||
        (latestJob ? getTranslation(latestJob.duration, lang) : ""),
      icon: Briefcase,
    },
    {
      tab: "academic",
      title: t.academicTitle,
      content:
        getHighlightOverride(settings, `home_highlight_acad_content_${lang}`, lang) ||
        (latestEducation ? getTranslation(latestEducation.role, lang) : ""),
      description:
        getHighlightOverride(settings, `home_highlight_acad_desc_${lang}`, lang) ||
        (latestEducation ? getTranslation(latestEducation.duration, lang) : ""),
      icon: GraduationCap,
    },
    {
      tab: "hobby",
      title: t.interestsTitle,
      content:
        getHighlightOverride(settings, `home_highlight_hobby_content_${lang}`, lang) ||
        t.interestsContent,
      description:
        getHighlightOverride(settings, `home_highlight_hobby_desc_${lang}`, lang) ||
        t.interestsDesc,
      icon: Heart,
    },
    {
      tab: "community",
      title: t.volunteeringTitle,
      content:
        getHighlightOverride(settings, `home_highlight_comm_content_${lang}`, lang) ||
        (latestVolunteering ? getTranslation(latestVolunteering.company, lang) : ""),
      description:
        getHighlightOverride(settings, `home_highlight_comm_desc_${lang}`, lang) ||
        (latestVolunteering ? getTranslation(latestVolunteering.role, lang) : ""),
      icon: Users2,
    },
  ];
}

function BentoHighlightsSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-pulse">
      <div className="md:col-span-2 border border-border/40 bg-card/45 rounded-2xl h-[180px]" />
      <div className="md:col-span-1 border border-border/40 bg-card/45 rounded-2xl h-[180px]" />
      <div className="md:col-span-1 border border-border/40 bg-card/45 rounded-2xl h-[180px]" />
      <div className="md:col-span-2 border border-border/40 bg-card/45 rounded-2xl h-[180px]" />
    </div>
  );
}

function TestimonialsSkeleton() {
  return (
    <div className="border border-border/40 bg-card/45 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row gap-6 items-center md:items-start h-[220px] animate-pulse">
      <div className="h-20 w-20 md:h-24 md:w-24 bg-muted rounded-full shrink-0" />
      <div className="flex-1 space-y-3 w-full text-left">
        <div className="h-5 w-32 bg-muted rounded" />
        <div className="h-4 w-48 bg-muted rounded" />
        <div className="space-y-2 pt-2">
          <div className="h-4 w-full bg-muted rounded" />
          <div className="h-4 w-5/6 bg-muted rounded" />
        </div>
      </div>
    </div>
  );
}

export function HomeTab({ lang }: HomeTabProps) {
  const t = getPageTranslations(lang).home;
  const navigate = useNavigate();
  const { settings, timeline, testimonials, loading } = useDynamicContent();

  if (loading) return <HomeSkeleton />;

  const rawPunchline = settings.find((s) => s.key === `home_punchline_${lang}`)?.value || t.punchline;
  const punchline = rawPunchline.replace(/\\n/g, "\n");
  const bio = settings.find((s) => s.key === `home_bio_${lang}`)?.value || t.bio;
  const profileImage = settings.find((s) => s.key === "home_profile_image")?.value || "";

  const profEvents = getTimelineEvents("professional", timeline, professionalTimeline);
  const latestJob = profEvents[0];

  const acadEvents = getTimelineEvents("academic", timeline, academicTimeline);
  const latestEducation = acadEvents[0];

  const commEvents = getTimelineEvents("community", timeline, communityTimeline);
  const latestVolunteering = commEvents[0];

  const highlights = buildHighlights(
    t,
    settings,
    lang,
    latestJob,
    latestEducation,
    latestVolunteering
  );

  return (
    <div className="space-y-16 max-w-7xl mx-auto py-6 animate-fade-in">
      {/* Intro Section */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center text-center lg:text-left">
        <div className="lg:col-span-7 space-y-6">
          {/* Hero Punchline with auto-fit responsive single-line behavior */}
          <AutoFitPunchline punchline={punchline} />
          <p className="text-base md:text-lg text-foreground/80 leading-relaxed whitespace-pre-wrap font-sans">
            {bio}
          </p>
          <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-2">
            <Button
              onClick={() => navigate(`/${lang}/contact`)}
              className="bg-brand-primary text-white hover:bg-brand-primary/95 font-semibold px-6 py-2 h-11"
            >
              <Mail className="h-4.5 w-4.5 mr-2" />
              {t.getInTouchBtn}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/${lang}/professional`)}
              className="border-border/80 hover:bg-muted font-semibold px-6 py-2 h-11"
            >
              {t.exploreJourneyBtn}
            </Button>
          </div>
        </div>

        {/* Right side: Photo avatar + Terminal pipeline mockup */}
        <div className="lg:col-span-5 flex flex-col gap-6 items-center lg:items-end w-full">
          <div className="relative group w-100 h-100 rounded-2xl overflow-hidden shadow-xl border-4 border-card bg-card/50 transition-all duration-500 hover:scale-120">
            {profileImage ? (
              <img
                src={profileImage}
                alt="Uğurcan Uzunkaya"
                className="w-full h-full object-cover object-center"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-tr from-brand-primary/20 via-brand-secondary/10 to-brand-primary/10 flex flex-col items-center justify-center p-3 text-center text-muted-foreground">
                <Users2 className="h-6 w-6 text-brand-primary mb-1" />
                <span className="text-[9px] font-semibold text-foreground/80">No image</span>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-brand-primary/10 to-transparent pointer-events-none" />
          </div>
        </div>
      </section>

      {/* Highlights Grid - Yields 0.5s late */}
      <SimulatedLoading fallback={<BentoHighlightsSkeleton />} delay={500}>
        <HighlightsGrid highlights={highlights} t={t} lang={lang} navigate={navigate} />
      </SimulatedLoading>

      {/* Testimonials Slider - Lazy loaded on scroll + Yields 0.5s late */}
      <LazyRevealSection fallback={<TestimonialsSkeleton />} delay={500}>
        <TestimonialsSection testimonials={testimonials} lang={lang} t={t} />
      </LazyRevealSection>
    </div>
  );
}

export default HomeTab;
